import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useConsent } from '@/contexts/ConsentContext';

const COPY = {
  pt: {
    title: 'Sua privacidade',
    body: 'Nosso site utiliza cookies para melhorar a navegação.',
    learnMore: 'Saiba mais',
    accept: 'Aceitar',
    reject: 'Rejeitar',
    ariaLabel: 'Aviso de privacidade e cookies',
  },
  es: {
    title: 'Tu privacidad',
    body: 'Nuestro sitio utiliza cookies para mejorar la navegación.',
    learnMore: 'Más información',
    accept: 'Aceptar',
    reject: 'Rechazar',
    ariaLabel: 'Aviso de privacidad y cookies',
  },
  en: {
    title: 'Your privacy',
    body: 'Our site uses cookies to improve navigation.',
    learnMore: 'Learn more',
    accept: 'Accept',
    reject: 'Reject',
    ariaLabel: 'Privacy and cookies notice',
  },
} as const;

export const ConsentBanner = () => {
  const { language } = useLanguage();
  const { shouldShowBanner, accept, reject } = useConsent();
  const [visible, setVisible] = useState(false);

  // Render condicional após mount para evitar flash durante hidratação inicial.
  useEffect(() => {
    if (shouldShowBanner) {
      const id = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [shouldShowBanner]);

  if (!shouldShowBanner) return null;

  const copy = COPY[language] ?? COPY.pt;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={copy.ariaLabel}
      className={`fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6 transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 p-5 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-foreground">{copy.title}</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {copy.body}{' '}
              <Link to="/politica-de-cookies" className="font-medium text-foreground underline underline-offset-4 hover:text-mint">
                {copy.learnMore}
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-shrink-0">
            <Button
              variant="outline"
              size="default"
              onClick={reject}
              className="w-full sm:w-auto"
            >
              {copy.reject}
            </Button>
            <Button
              variant="default"
              size="default"
              onClick={accept}
              className="w-full sm:w-auto"
            >
              {copy.accept}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
